import { isEqual } from 'underscore';
import { sanitizeScene, applyAnimations } from '@studio/program';
import { BROADCAST_SET_PROGRAM } from 'actions/Types';
import { withCurrentParams } from 'utils/actionUtils';
import { addSignedUrlsToBroadcast } from '@shoflo/studio-redux/utils';
import agoraOnstageAttendeeUpdater from '@cvent/studio-agora/metrics/agoraOnstageAttendeeUpdater';
import { shouldHaveFeature } from '@studio/feature.flags';
import { VENDOR_AGORA } from '@studio/constants';
import handleAgoraOnStagePriority from '@shoflo/studio-redux/actions/meeting/handleAgoraOnStagePriority';
import { sendToDatadog } from '@studio/logger';

const loggerName = 'app.studio.director.lite.setProgram';
/**
 * @param scene object the scene to set the program to
 * @param isTransient boolean whether we should save this to the API or not, if isTransient is true, persistence is not desired.
 * @param otherConfig Object containing other configs
 */
function setProgram(
  scene,
  isTransient,
  otherConfig = { emitSocket: true },
  endActionCallback = null
) {
  return withCurrentParams(
    (dispatch, { teamId, eventId, broadcastId }, getState) => {
      const { program, overlays, backgrounds, sceneLayers } = getState();

      const { updatedSceneLayers } = otherConfig;
      /**
       * There is an instance where there is no change to a scene,
       * we shouldn't update the program because it causes undesired change to broadcast sources
       */
      if (isEqual(scene, program)) return;

      const action = {
        type: BROADCAST_SET_PROGRAM,
        payload: applyAnimations(program, scene),
        sceneLayerSources: { overlays, backgrounds },
        request: async apiClient => {
          try {
            //Deep clone, because this appears to mutate the parameter
            const scenePayload = sanitizeScene(
              JSON.parse(JSON.stringify(scene))
            );
            let broadcast = await apiClient.broadcast.updateProgram(
              teamId,
              eventId,
              broadcastId,
              scenePayload
            );

            /**
             * API only returns ` overlays, backgrounds, globalSceneLayers`
             * only when we update program from scene.
             * If these parameters are not returned, we get them from state.
             */
            if (!broadcast.overlays) {
              broadcast = {
                ...broadcast,
                overlays: overlays
              };
            }

            if (!broadcast.backgrounds) {
              broadcast = {
                ...broadcast,
                backgrounds: backgrounds
              };
            }

            if (!broadcast.globalSceneLayers) {
              broadcast.globalSceneLayers = updatedSceneLayers ?? sceneLayers;
            }

            // Assigning broadcast._id since it is not returned in the response of the broadcast.updateProgram API, ensuring it is available for the further update call
            broadcast._id = broadcastId;
            const broadcastWithSignedUrls = await addSignedUrlsToBroadcast(
              { ...broadcast, team: teamId, event: eventId },
              apiClient,
              teamId,
              eventId
            );
            if (typeof endActionCallback === 'function') {
              endActionCallback();
            }

            return broadcastWithSignedUrls;
          } catch (error) {
            if (typeof endActionCallback === 'function') {
              endActionCallback({ error: error.message });
            }
            throw error;
          }
        },
        isTransient,
        otherConfig
      };
      // If it's transient, don't save to API...
      if (isTransient || action.isTransient) delete action.request;
      // filter the program just to the relevant sources / background for logging
      const programLog = {};
      programLog.sources = scene?.layers
        ?.filter(e => e.source)
        .map(e => ({
          id: e.source.id,
          type: e.source.type,
          value: e.source.value
        }));
      programLog.background = scene?.style;
      programLog.emitSocket = otherConfig.emitSocket;
      sendToDatadog(
        loggerName,
        {
          program: programLog
        },
        'Program update initialized via setProgram'
      );
      if (shouldHaveFeature('studio.meetingVendor') === VENDOR_AGORA) {
        const { meeting } = getState();
        agoraOnstageAttendeeUpdater(scene, meeting);
        dispatch(handleAgoraOnStagePriority({ payload: scene }));
      }
      dispatch(action);
    }
  );
}

export default setProgram;
